import type { PlanTemplate, ProgramTemplate } from "./templateSchema.js";
import { entry, repSets } from "./templateAuthoring.js";

// ⚠️ PURE DATA — no logic lives here, deliberately: the authoring helpers it
// calls are in templateAuthoring.ts and the schemas in templateSchema.ts. If
// you find yourself writing a function here, it belongs in one of those two.
//
// This file holds the PLAN templates inline and assembles the PROGRAM array
// from one file per program. The first cut of SP-TEMPLATES kept all four
// templates here and claimed the data-file exemption to the 500-line cap
// (next/prompt.md §Limiti di dimensione file) at 651 lines. Two reviewers
// pushed back and they were right: that exemption's worked examples are
// generated/JSON/inert content, whereas this is hand-authored TS that CALLS
// factories to derive its values, and the result would have been the single
// largest file in the repo. So the data is namespaced per program instead —
// which is the escape hatch the standard itself names ("si namespace-ano se
// diventano ingombranti") and needs no exemption argument at all.

// PUBLIC TEMPLATES — common, beginner-friendly starter content visible
// to every signed-in user. Source of truth lives here in shared so the
// web client and the API both render identical data without a DB round
// trip; the GET /api/templates endpoints serialise this verbatim.
//
// Why static: at family scale the templates change at deploy cadence,
// not at request cadence. A DDB partition for templates would buy
// runtime mutability we don't need yet — and would add admin auth +
// migration plumbing for content that already version-controls cleanly
// in git. If we later want admin-managed templates, swap the in-memory
// arrays for a DDB read; the API and web shapes don't change.
//
// `id` is hardcoded so a clone-from-template path can dedupe by source
// (future "you've already cloned this" hint). The template id is NOT
// the cloned plan/program id — clones get a fresh client UUID.
//
// MULTI-LANGUAGE (1.50.0): every user-facing string on a TEMPLATE is
// `LocalizedText` — EN required as the deterministic fallback, IT / FR
// / DE / RU optional. The runtime Plan / Program / ProgramSession /
// Slot schemas stay single-string (those are user-typed); the
// resolveXTemplate helpers below collapse the LocalizedText fields
// into the runtime shape using the renderer's locale, both for clone-
// time POST bodies and for inline display in the templates picker.

// ─── PLAN TEMPLATES ────────────────────────────────────────────────────

const fullBodyBeginner: PlanTemplate = {
  id: "tpl-plan-full-body-beginner",
  name: {
    en: "Full Body — Beginner",
    it: "Full Body — Principianti",
    fr: "Full Body — Débutant",
    de: "Ganzkörper — Anfänger",
    ru: "Full Body — Начинающий",
  },
  description: {
    en: "Single-day starter session for new lifters. Four compound lifts, three sets of ten, target a moderate 7 RPE. Use it twice a week with at least 48 hours between sessions.",
    it: "Sessione singola per chi inizia. Quattro fondamentali, tre serie da dieci, target a un RPE 7 moderato. Usala due volte a settimana con almeno 48 ore tra una sessione e l'altra.",
    fr: "Séance unique pour les débutants. Quatre exercices polyarticulaires, trois séries de dix, cible un RPE 7 modéré. À utiliser deux fois par semaine avec au moins 48 heures entre les séances.",
    de: "Einzelne Einstiegseinheit für neue Athleten. Vier Grundübungen, drei Sätze à zehn Wiederholungen, Ziel ist ein moderater RPE 7. Zweimal pro Woche einsetzen, mit mindestens 48 Stunden Pause zwischen den Einheiten.",
    ru: "Одиночная стартовая тренировка для новичков. Четыре базовых упражнения, три подхода по десять, целевой RPE 7 — умеренный. Используй дважды в неделю с минимум 48 часами между тренировками.",
  },
  difficulty: "beginner",
  tags: ["full-body", "beginner", "compound"],
  notes: {
    en: "Pick a weight you can complete every set with two reps in reserve. Add 2–5 kg next session if all sets feel solid.",
    it: "Scegli un peso che ti permetta di chiudere ogni serie con due ripetizioni di riserva. Aggiungi 2–5 kg alla prossima sessione se tutte le serie sono solide.",
    fr: "Choisis une charge qui te permet de finir chaque série avec deux répétitions en réserve. Ajoute 2–5 kg à la séance suivante si toutes les séries passent solidement.",
    de: "Gewicht wählen, mit dem jeder Satz mit zwei Wiederholungen in Reserve abgeschlossen werden kann. Beim nächsten Training 2–5 kg drauflegen, wenn alle Sätze sauber laufen.",
    ru: "Выбери вес, с которым закроешь каждый подход с двумя повторами в запасе. Прибавь 2–5 кг на следующей тренировке, если все подходы прошли уверенно.",
  },
  entries: [
    entry(
      "back-squat",
      "Back Squat",
      repSets(3, 10, { kind: "absolute", weightKg: 0 }, 7),
    ),
    entry(
      "bench-press",
      "Bench Press",
      repSets(3, 10, { kind: "absolute", weightKg: 0 }, 7),
    ),
    entry(
      "barbell-row",
      "Barbell Row",
      repSets(3, 10, { kind: "absolute", weightKg: 0 }, 7),
    ),
    entry(
      "overhead-press",
      "Overhead Press",
      repSets(3, 10, { kind: "absolute", weightKg: 0 }, 7),
    ),
  ],
};

const pushHypertrophy: PlanTemplate = {
  id: "tpl-plan-push-hypertrophy",
  name: {
    en: "Push Day — Hypertrophy",
    it: "Push Day — Ipertrofia",
    fr: "Push Day — Hypertrophie",
    de: "Push-Tag — Hypertrophie",
    ru: "Push Day — Гипертрофия",
  },
  description: {
    en: "Chest / shoulders / triceps day for a push-pull-legs split. Volume-led: top set heavier, back-off and isolation lighter. Plug it into a 4–6× / week split.",
    it: "Giornata petto / spalle / tricipiti per una split push-pull-legs. Spinta sul volume: top set più pesante, back-off e isolamento più leggeri. Inseriscila in una split 4–6× / settimana.",
    fr: "Séance pectoraux / épaules / triceps pour une split push-pull-legs. Axée volume : top set plus lourd, back-off et isolation plus légers. À intégrer dans une split 4–6× / semaine.",
    de: "Brust- / Schulter- / Trizeps-Tag für eine Push-Pull-Legs-Split. Volumenorientiert: schwerer Top-Satz, Back-off und Isolation leichter. In eine 4–6× / Woche-Split integrieren.",
    ru: "День грудь / плечи / трицепс для сплита push-pull-legs. Упор на объём: топ-сет тяжелее, бэк-офф и изоляция легче. Встраивается в сплит 4–6× в неделю.",
  },
  difficulty: "intermediate",
  tags: ["push", "hypertrophy", "chest", "shoulders"],
  notes: {
    en: "Rest 2–3 min on the compounds, 60–90 s on isolation work.",
    it: "Riposo 2–3 min sui fondamentali, 60–90 s sull'isolamento.",
    fr: "Repos 2–3 min sur les polyarticulaires, 60–90 s sur les exercices d'isolation.",
    de: "2–3 min Pause bei den Grundübungen, 60–90 s bei der Isolationsarbeit.",
    ru: "Отдых 2–3 мин на базовых, 60–90 с на изоляции.",
  },
  entries: [
    entry(
      "bench-press",
      "Bench Press",
      [
        { targetReps: 8, targetWeight: { kind: "percentage", pctOfMax: 75 } },
        { targetReps: 8, targetWeight: { kind: "percentage", pctOfMax: 72 } },
        { targetReps: 8, targetWeight: { kind: "percentage", pctOfMax: 70 } },
        { targetReps: 10, targetWeight: { kind: "percentage", pctOfMax: 65 } },
      ],
    ),
    entry(
      "incline-bench-press",
      "Incline Bench Press",
      repSets(3, 10, { kind: "percentage", pctOfMax: 60 }),
    ),
    entry(
      "overhead-press",
      "Overhead Press",
      repSets(3, 10, { kind: "percentage", pctOfMax: 65 }, 8),
    ),
    entry(
      "lateral-raise",
      "Lateral Raise",
      repSets(3, 12, { kind: "absolute", weightKg: 0 }, 8),
      {
        en: "Form first; keep the elbow above the wrist at the top.",
        it: "Tecnica prima di tutto; mantieni il gomito sopra il polso in chiusura.",
        fr: "La technique d'abord ; garde le coude au-dessus du poignet en haut du mouvement.",
        de: "Technik zuerst; den Ellenbogen am oberen Punkt über dem Handgelenk halten.",
        ru: "Сначала техника; в верхней точке локоть держи выше запястья.",
      },
    ),
  ],
};

export const PUBLIC_PLAN_TEMPLATES: PlanTemplate[] = [
  fullBodyBeginner,
  pushHypertrophy,
];

// ─── PROGRAM TEMPLATES ─────────────────────────────────────────────────
import { stronglifts5x5 } from "./templateProgramStronglifts.js";
import { pushPullLegs } from "./templateProgramPushPullLegs.js";

export const PUBLIC_PROGRAM_TEMPLATES: ProgramTemplate[] = [
  stronglifts5x5,
  pushPullLegs,
];
